import React, { useState, useRef, useMemo } from 'react';
import { Upload, AlertTriangle, XCircle, CheckCircle2, Download } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { purchasesAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';

// Rows the user is allowed to tick. Everything else is reported only.
const SELECTABLE = ['ready', 'merged', 'parent_product'];
// Rows that could not be imported, as opposed to ones the user unticked.
const FAILED = ['unmatched', 'invalid_id', 'invalid_quantity'];

const STATUS_LABEL = {
  ready: 'Ready',
  merged: 'Merged duplicate',
  parent_product: 'Parent product',
  unmatched: 'Not found',
  invalid_id: 'Invalid ID',
  invalid_quantity: 'Invalid quantity',
};

// CSV rows a preview row stands for: itself plus any duplicates folded in.
const rowsRepresented = (row) => 1 + (row.merged_from?.length || 0);

const ImportCsvModal = ({ isOpen, onClose, onConfirm }) => {
  const { formatPrice } = useCurrency();
  const fileInputRef = useRef(null);

  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('');

  const reset = () => {
    setPreview(null);
    setSelected({});
    setError(null);
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    // Clear immediately so re-selecting the same filename fires onChange again
    // (the browser suppresses the event when the path is unchanged).
    event.target.value = '';
    if (!file) return;

    setFileName(file.name);
    setIsUploading(true);
    setError(null);
    setPreview(null);

    try {
      const response = await purchasesAPI.importPreview(file);
      const data = response.data;
      setPreview(data);
      // Tick everything importable except parent products, which are opt-in.
      const initial = {};
      data.rows.forEach((row) => {
        initial[row.row_number] = row.status === 'ready' || row.status === 'merged';
      });
      setSelected(initial);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not read that file.');
    } finally {
      setIsUploading(false);
    }
  };

  const toggle = (rowNumber) => {
    setSelected((prev) => ({ ...prev, [rowNumber]: !prev[rowNumber] }));
  };

  const counts = useMemo(() => {
    if (!preview) return { imported: 0, failed: 0, skipped: 0 };
    let imported = 0;
    let failed = 0;
    let skipped = 0;
    preview.rows.forEach((row) => {
      const n = rowsRepresented(row);
      if (FAILED.includes(row.status)) failed += n;
      else if (selected[row.row_number]) imported += n;
      else skipped += n;
    });
    return { imported, failed, skipped };
  }, [preview, selected]);

  const selectedRows = useMemo(
    () =>
      preview
        ? preview.rows.filter(
            (row) => SELECTABLE.includes(row.status) && selected[row.row_number]
          )
        : [],
    [preview, selected]
  );

  const unmatchedRows = useMemo(
    () => (preview ? preview.rows.filter((row) => row.status === 'unmatched') : []),
    [preview]
  );

  // Rebuild a CSV of just the unmatched rows, in the original column order,
  // so the user can re-upload them once those products have synced.
  const downloadUnmatched = () => {
    const header = 'ID,Type,Name,Quantity,Sale price,Regular price';
    const escape = (value) => {
      const text = String(value ?? '');
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const lines = unmatchedRows.map((row) =>
      [row.csv_id, row.csv_type, row.csv_name, row.quantity, '', ''].map(escape).join(',')
    );
    const blob = new Blob([[header, ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `unmatched-${fileName || 'rows.csv'}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleConfirm = () => {
    onConfirm({
      rows: selectedRows,
      summary: preview.summary,
      importedRowCount: counts.imported,
      failedRowCount: counts.failed,
      skippedRowCount: counts.skipped,
    });
    reset();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import purchase from CSV" size="full">
      {!preview && (
        <div className="text-center py-8">
          {/* Kept mounted while verifying so the ref stays valid for the retry click. */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
          />

          {isUploading ? (
            <div className="py-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-sm font-medium text-gray-900">
                Verifying {fileName || 'your file'}…
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Matching every row against your items and checking quantities.
                Nothing is saved until you confirm.
              </p>
            </div>
          ) : (
            <>
              <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Choose a CSV file
              </button>
              <p className="text-xs text-gray-500 mt-3">
                Needs an <code>ID</code> column of product IDs and a <code>Quantity</code> column.
                Prices come from the item record, not the file.
              </p>
              {error && (
                <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                  {error}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {preview && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium text-gray-900">{fileName}</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 text-green-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> {counts.imported} to import
            </span>
            {counts.failed > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 text-red-700">
                <XCircle className="h-3.5 w-3.5" /> {counts.failed} failed
              </span>
            )}
            {counts.skipped > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                {counts.skipped} skipped
              </span>
            )}
            {preview.summary.warnings > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5" /> {preview.summary.warnings} warning
                {preview.summary.warnings === 1 ? '' : 's'}
              </span>
            )}
          </div>

          <div className="max-h-[55vh] overflow-y-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="w-10 p-2"></th>
                  <th className="p-2 text-left font-medium text-gray-700">Line</th>
                  <th className="p-2 text-left font-medium text-gray-700">Item</th>
                  <th className="p-2 text-left font-medium text-gray-700">Barcode</th>
                  <th className="p-2 text-right font-medium text-gray-700">Qty</th>
                  <th className="p-2 text-right font-medium text-gray-700">Price</th>
                  <th className="p-2 text-left font-medium text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => {
                  const selectable = SELECTABLE.includes(row.status);
                  return (
                    <tr
                      key={row.row_number}
                      className={`border-t border-gray-100 ${selectable ? '' : 'bg-gray-50 text-gray-400'}`}
                    >
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          disabled={!selectable}
                          checked={Boolean(selected[row.row_number])}
                          onChange={() => toggle(row.row_number)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="p-2 text-gray-500">{row.row_number}</td>
                      <td className="p-2">
                        <div className="font-medium">
                          {row.item?.Name || row.csv_name || '—'}
                        </div>
                        {/*
                          The CSV's "ID" column is the WooCommerce product id,
                          which this system stores as the item's Barcode. Label
                          it Barcode, not ID — the item dropdown on this same
                          form uses "ID" for the internal Item.ID, so calling
                          both "ID" invites matching two unrelated numbers.
                        */}
                        <div className="text-xs text-gray-500">
                          CSV ID: {row.csv_id}
                          {row.message ? ` • ${row.message}` : ''}
                        </div>
                      </td>
                      {/*
                        The item's own barcode, straight from the database. It
                        normally equals the CSV's ID (product sync sets
                        Barcode = product_id), so showing both makes a
                        divergence visible instead of silent.
                      */}
                      <td className="p-2 font-mono text-xs">
                        {row.item?.Barcode || '—'}
                      </td>
                      <td className="p-2 text-right">{row.quantity}</td>
                      <td className="p-2 text-right">
                        {row.item ? formatPrice(row.item.Price) : '—'}
                      </td>
                      <td className="p-2">{STATUS_LABEL[row.status] || row.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              {unmatchedRows.length > 0 && (
                <button
                  type="button"
                  onClick={downloadUnmatched}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Download className="h-4 w-4" />
                  Download {unmatchedRows.length} unmatched row
                  {unmatchedRows.length === 1 ? '' : 's'}
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-3.5 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={selectedRows.length === 0}
                className="px-5 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Add {counts.imported} row{counts.imported === 1 ? '' : 's'} to purchase
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ImportCsvModal;
